import React, { useCallback, useContext, useEffect, useState } from 'react';
import Modal from '@atlaskit/modal-dialog';

import CurrentUserContext from 'contexts/CurrentUserContext';
import ToastContext from 'contexts/ToastContext';
import STORE from 'store';
import useCurrentAccountId from 'hooks/useCurrentAccountId';
import Form from 'kingpin/forms/Form';
import FormHeader from 'kingpin/forms/FormHeader';
import FormContent from 'kingpin/forms/FormContent';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Alert from 'kingpin/Alert';
import Row from 'components/Common/Row';
import Toggle from 'components/Inputs/Toggle';
import Loading from 'components/Loading/Loading';

// Staff toggle, per reward, whether it stays visible while override mode is
// active. Rewards are enabled (visible) by default; disabling one hides it from
// the slideout override view, entity-app bonus report tabs and the weekly
// override confirmation breakdown when overriding. Account-wide, per programme.
const BonusOverridesModal = ({
  bonusProgram,
  close,
}: {
  bonusProgram: BonusTypes.BonusProgram;
  close: () => void;
}) => {
  const accountId = useCurrentAccountId();
  const currentUser = useContext(CurrentUserContext);
  const { showToast } = useContext(ToastContext);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  // Reward `name`s the user has toggled off (hidden while overriding).
  const [hiddenRewardNames, setHiddenRewardNames] = useState<Set<string>>(
    new Set(),
  );
  // The Approvals Overview setting shares this doc; carry it through on save.
  const [hiddenOverviewRewardNames, setHiddenOverviewRewardNames] = useState<
    string[] | undefined
  >();

  useEffect(() => {
    let isActive = true;

    STORE.bonus
      .getBonusOverrideSettingsRef({
        accountId,
        bonusProgramId: bonusProgram.id,
      })
      .get()
      .then((snapshot) => {
        if (!isActive) return;
        setHiddenRewardNames(new Set(snapshot.data()?.hiddenRewardNames ?? []));
        setHiddenOverviewRewardNames(
          snapshot.data()?.hiddenOverviewRewardNames,
        );
        setIsLoading(false);
      })
      .catch(() => {
        if (!isActive) return;
        setError('Failed to load override settings');
        setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [accountId, bonusProgram.id]);

  const onToggle = useCallback((rewardName: string, isEnabled: boolean) => {
    setHiddenRewardNames((prev) => {
      const next = new Set(prev);
      if (isEnabled) {
        next.delete(rewardName);
      } else {
        next.add(rewardName);
      }
      return next;
    });
  }, []);

  const onSave = useCallback(async () => {
    setIsSaving(true);
    setError(undefined);

    const now = new Date().toISOString();
    const config: BonusOverrideSettings.Config = {
      hiddenRewardNames: Array.from(hiddenRewardNames),
      ...(hiddenOverviewRewardNames ? { hiddenOverviewRewardNames } : {}),
      updatedBy: currentUser.id,
      updatedOn: now,
    };

    try {
      await STORE.bonus
        .getBonusOverrideSettingsRef({
          accountId,
          bonusProgramId: bonusProgram.id,
        })
        .set(config);
      setIsSaving(false);
      showToast('Override settings updated');
      close();
    } catch {
      setIsSaving(false);
      setError('Failed to save override settings');
    }
  }, [
    accountId,
    bonusProgram.id,
    close,
    currentUser.id,
    hiddenOverviewRewardNames,
    hiddenRewardNames,
    showToast,
  ]);

  return (
    <Modal
      onClose={close}
      shouldScrollInViewport={false}
      autoFocus={false}
      width={600}
    >
      <Form>
        <FormHeader
          title={'Overrides'}
          subTitle={bonusProgram.name}
          onClose={close}
        />
        <FormContent>
          <div style={{ marginBottom: 16 }}>
            <Alert
              type={'Info'}
              body={
                'These settings are account-wide. They control which rewards stay visible in override mode for this programme everywhere — driver bonus screens, board slideout overrides and the weekly override confirmation — for every user in this account.'
              }
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <Typography.Body type={'Body 12'}>
              {
                'Enabled rewards stay visible while overriding. Disable a reward to hide it when override mode is entered — it still shows normally otherwise.'
              }
            </Typography.Body>
          </div>
          {isLoading && <Loading isSmall />}
          {error && !isLoading && <Alert type={'Danger'} body={error} />}
          {!isLoading && !error && bonusProgram.rewardFields.length === 0 && (
            <Alert type={'Info'} body={'This program has no rewards'} />
          )}
          {!isLoading &&
            !error &&
            bonusProgram.rewardFields.map((rewardField) => (
              <Row
                key={rewardField.name}
                spaceBetween
                centerAlign
                style={{ marginBottom: 8 }}
              >
                <Typography.Body type={'Body 13'}>
                  {rewardField.displayName}
                </Typography.Body>
                <Toggle
                  testId={`override-reward-toggle-${rewardField.name}`}
                  value={!hiddenRewardNames.has(rewardField.name)}
                  onChange={(isEnabled) =>
                    onToggle(rewardField.name, isEnabled)
                  }
                />
              </Row>
            ))}
        </FormContent>
        <Row spaceBetween centerAlign style={{ padding: '12px 16px' }}>
          <Button
            size={'Medium'}
            type={'Secondary'}
            label={'Cancel'}
            onClick={close}
          />
          <Button
            size={'Medium'}
            type={'Primary'}
            label={'Save'}
            onClick={onSave}
            isLoading={isSaving}
            isDisabled={isLoading || isSaving || !!error}
          />
        </Row>
      </Form>
    </Modal>
  );
};

export default BonusOverridesModal;
